// ============================================================
//  hooks/useYouTubePlayer.ts
//  YouTube IFrame Player API — 공식 최신 방식 (2025)
//  https://developers.google.com/youtube/iframe_api_reference
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  YTPlayer,
  YTPlayerOptions,
  YTPlayerVars,
  YTPlayerStateChangeEvent,
  YTPlayerErrorEvent,
  YTPlaybackQuality,
  LogEntry,
  LogType,
} from '../types/ytmusic';
import { YTPlayerState, YTPlayerError } from '../types/ytmusic';

// ── IFrame API 싱글턴 로더 ────────────────────────────────────
// 모듈 스코프에서 단 하나의 Promise 만 유지하여 스크립트 중복 삽입을 방지합니다.
let _apiPromise: Promise<void> | null = null;

/**
 * YouTube IFrame API 스크립트를 싱글턴으로 로드합니다.
 *
 * - 이미 `window.YT.Player` 가 존재하면 즉시 resolve 합니다.
 * - 기존에 등록된 `window.onYouTubeIframeAPIReady` 콜백이 있다면 체이닝하여 유지합니다.
 * - `<script id="yt-iframe-api">` 태그가 없을 때만 DOM 에 삽입합니다.
 */
function loadYouTubeIframeAPI(): Promise<void> {
  // 이미 로드 중이거나 완료된 Promise 가 있으면 재사용합니다.
  if (_apiPromise) return _apiPromise;
  _apiPromise = new Promise<void>((resolve) => {
    // API 가 이미 로드되어 있으면 즉시 완료 처리합니다.
    if (window.YT?.Player) { resolve(); return; }

    // 기존에 등록된 전역 콜백을 보존하여 다른 컴포넌트와 충돌하지 않도록 합니다.
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(); };

    // 스크립트가 아직 삽입되지 않은 경우에만 추가합니다.
    if (!document.getElementById('yt-iframe-api')) {
      const s   = document.createElement('script');
      s.id      = 'yt-iframe-api';
      s.src     = 'https://www.youtube.com/iframe_api';
      s.async   = true;
      document.head.appendChild(s);
    }
  });
  return _apiPromise;
}


/**
 * YT.Player 오류 코드를 사람이 읽기 쉬운 한국어 메시지로 변환합니다.
 *
 * @param code - YTPlayerError 열거형 코드
 * @returns 오류 설명 문자열
 */
function errorMessage(code: number): string {
  switch (code) {
    case YTPlayerError.INVALID_PARAM:      return '잘못된 videoId 파라미터 (2)';
    case YTPlayerError.HTML5_ERROR:        return 'HTML5 플레이어 오류 (5)';
    case YTPlayerError.NOT_FOUND:          return '영상을 찾을 수 없음 (100)';
    case YTPlayerError.NOT_EMBEDDABLE:
    case YTPlayerError.NOT_EMBEDDABLE_ALT: return '임베드 비허용 영상 (101/150)';
    default:                               return `알 수 없는 오류 (${code})`;
  }
}

// ── 훅 옵션 ──────────────────────────────────────────────────
/**
 * `useYouTubePlayer` 훅 초기화 옵션.
 */
export interface UseYouTubePlayerOptions {
  /** 훅 마운트 시 자동으로 로드할 초기 영상 ID */
  initialVideoId?: string;
  /** DEFAULT_VARS 에 병합할 추가 YT.PlayerVars 설정 */
  playerVars?:     Partial<YTPlayerVars>;
  /** 플레이어가 준비되었을 때 호출되는 콜백 */
  onReady?:        (player: YTPlayer) => void;
  /** 플레이어 상태가 변경될 때 호출되는 콜백 */
  onStateChange?:  (state: YTPlayerState, player: YTPlayer) => void;
  /** 오류 발생 시 호출되는 콜백 (videoId 포함) */
  onError?:        (code: number, msg: string, videoId: string) => void;
  /** 영상 재생이 종료되었을 때 호출되는 콜백 */
  onEnded?:        (player: YTPlayer) => void;
}

// ── 훅 반환 타입 ─────────────────────────────────────────────
/**
 * `useYouTubePlayer` 훅의 반환 값 타입.
 * 플레이어 상태와 모든 제어 함수를 포함합니다.
 */
export interface UseYouTubePlayerReturn {
  /** 플레이어 컨테이너 ref — JSX div에 붙임 */
  containerRef:    React.RefObject<HTMLDivElement | null>;
  /** YT.Player 인스턴스 */
  player:          YTPlayer | null;
  /** 현재 YTPlayerState */
  playerState:     YTPlayerState | null;
  /** API+플레이어 준비 완료 */
  isReady:         boolean;
  /** 재생 중 여부 */
  isPlaying:       boolean;
  /** 현재 볼륨 0-100 */
  volume:          number;
  /** 음소거 여부 */
  isMuted:         boolean;
  /** 이벤트 로그 (최대 100개, 최신순) */
  logs:            LogEntry[];
  // ── 제어 함수 ──────────────────────────────────────────────
  /** 영상을 즉시 로드하고 재생합니다. */
  loadVideo:       (videoId: string, startSeconds?: number) => void;
  /** 영상을 큐에 올려두고 자동 재생하지 않습니다. */
  cueVideo:        (videoId: string, startSeconds?: number) => void;
  /** 재생목록을 로드합니다. */
  loadPlaylist:    (playlistId: string, index?: number) => void;
  /** 재생합니다. */
  play:            () => void;
  /** 일시정지합니다. */
  pause:           () => void;
  /** 재생을 중지합니다. */
  stop:            () => void;
  /** 특정 시간으로 이동합니다. */
  seekTo:          (seconds: number, allowSeekAhead?: boolean) => void;
  /** 재생목록의 다음 영상으로 이동합니다. */
  next:            () => void;
  /** 재생목록의 이전 영상으로 이동합니다. */
  previous:        () => void;
  /** 볼륨을 0~100 사이 값으로 설정합니다. */
  setVolume:       (vol: number) => void;
  /** 음소거/음소거 해제를 토글합니다. */
  toggleMute:      () => void;
  /** 재생 속도를 설정합니다. (예: 0.5, 1, 1.5, 2) */
  setPlaybackRate: (rate: number) => void;
  /** 재생 화질을 설정합니다. */
  setQuality:      (q: YTPlaybackQuality) => void;
  /** 재생목록 반복 재생을 설정합니다. */
  setLoop:         (loop: boolean) => void;
  /** 현재 재생 위치(초)를 반환합니다. */
  getCurrentTime:  () => number;
  /** 영상 전체 길이(초)를 반환합니다. */
  getDuration:     () => number;
  /** 버퍼링 완료 비율(0~1)을 반환합니다. */
  getLoadedFraction: () => number;
  /** 현재 재생목록 인덱스를 반환합니다. */
  getPlaylistIndex: () => number;
  /** 현재 영상의 YouTube URL을 반환합니다. */
  getVideoUrl:     () => string;
  /** 플레이어 인스턴스를 완전히 파괴합니다. */
  destroy:         () => void;
  /** iframe을 파괴하고 새로 생성합니다 (GPU 메모리 해제). 새 플레이어가 ready 되면 resolve. */
  recreatePlayer:  () => Promise<void>;
}


// ── 기본 playerVars ──────────────────────────────────────────
/**
 * 모든 플레이어에 공통으로 적용되는 기본 YT.PlayerVars 설정입니다.
 * `UseYouTubePlayerOptions.playerVars` 로 개별 항목을 덮어쓸 수 있습니다.
 */
const DEFAULT_VARS: YTPlayerVars = {
  enablejsapi: 1,
  origin: typeof window !== 'undefined' ? window.location.origin : '',
  rel:            0,
  controls:       1,
  playsinline:    1,
  fs:             1,
  iv_load_policy: 3,
  modestbranding: 1,
} as YTPlayerVars;

// ── 훅 ───────────────────────────────────────────────────────
export function useYouTubePlayer(
    options: UseYouTubePlayerOptions = {}
): UseYouTubePlayerReturn {
  const {
    initialVideoId,
    playerVars: extraVars = {},
  } = options;

  // 플레이어가 마운트될 div 를 가리키는 ref
  const containerRef = useRef<HTMLDivElement>(null);
  // YT.Player 인스턴스를 ref 로 관리하여 리렌더링 없이 최신 인스턴스를 유지합니다.
  const playerRef    = useRef<YTPlayer | null>(null);
  // 콜백 함수들을 ref 로 최신 상태로 유지합니다. (의존성 배열 없이 최신 값 참조)
  const cbRef        = useRef(options);
  cbRef.current      = options;

  // 현재 로드 중인 videoId 또는 playlistId를 추적하여 에러 시 로그에 포함
  const lastLoadedRef = useRef<{ type: 'video' | 'playlist'; id: string; index?: number } | null>(null);

  const [player,      setPlayer]      = useState<YTPlayer | null>(null);
  const [playerState, setPlayerState] = useState<YTPlayerState | null>(null);
  const [isReady,     setIsReady]     = useState(false);
  const [volume,      setVolumeState] = useState(100);
  const [isMuted,     setIsMuted]     = useState(false);
  const [logs]                        = useState<LogEntry[]>([]);

  const addLog = useCallback((_type: LogType, _message: string): LogEntry => {
    return { id: 0, time: '', type: _type, message: _message };
  }, []);

  const handleError = useCallback((code: number, playerInstance: YTPlayer | null) => {
    const msg = errorMessage(code);
    let videoId = '';
    
    try {
      const url = playerInstance?.getVideoUrl?.() ?? '';
      const match = url.match(/[?&]v=([^&]+)/);
      videoId = match?.[1] ?? '';
    } catch {
      // ignore
    }
    
    const lastLoaded = lastLoadedRef.current;
    const debugInfo = lastLoaded 
      ? `[${lastLoaded.type}=${lastLoaded.id}${lastLoaded.index !== undefined ? `, idx=${lastLoaded.index}` : ''}]`
      : '';
    
    console.error(`[YT Error] code=${code}, msg=${msg}, videoId=${videoId || 'unknown'} ${debugInfo}`);
    cbRef.current.onError?.(code, msg, videoId || lastLoaded?.id || 'unknown');
  }, []);

  const buildPlayerOpts = useCallback((): YTPlayerOptions => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vars = { ...DEFAULT_VARS, ...extraVars } as any;
    return {
      width: '100%',
      height: '100%',
      ...(initialVideoId ? { videoId: initialVideoId } : {}),
      playerVars: vars as YTPlayerVars,
      events: {
        onReady: (ev) => {
          const p = ev.target;
          playerRef.current = p;
          setPlayer(p);
          setIsReady(true);
          setVolumeState(p.getVolume());
          setIsMuted(p.isMuted());
          console.log('[useYouTubePlayer] Player ready (buildPlayerOpts)');
          cbRef.current.onReady?.(p);
        },
        onStateChange: (ev: YTPlayerStateChangeEvent) => {
          const s = ev.data;
          setPlayerState(s);
          cbRef.current.onStateChange?.(s, ev.target);
          if (s === YTPlayerState.ENDED) cbRef.current.onEnded?.(ev.target);
        },
        onError: (ev: YTPlayerErrorEvent) => {
          handleError(ev.data as number, ev.target);
        },
        onPlaybackQualityChange: () => {},
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  /** wrapper 내부에 새 mount div를 추가하고 YT.Player를 생성합니다. */
  const createMountAndPlayer = useCallback((wrapper: HTMLDivElement) => {
    wrapper.innerHTML = '';
    const mountDiv = document.createElement('div');
    mountDiv.style.width = '100%';
    mountDiv.style.height = '100%';
    wrapper.appendChild(mountDiv);

    const opts = buildPlayerOpts();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new window.YT.Player(mountDiv, opts as any);
  }, [buildPlayerOpts]);

  // ── 플레이어 초기화 ────────────────────────────────────────
  useEffect(() => {
    let alive = true;

    (async () => {
      await loadYouTubeIframeAPI();
      if (!alive || !containerRef.current || !window.YT?.Player) return;
      console.log('[useYouTubePlayer] Initial player creation');
      createMountAndPlayer(containerRef.current);
    })().catch((e) => {
      console.error('[useYouTubePlayer] Initial load error:', e);
    });

    return () => {
      alive = false;
      playerRef.current?.destroy();
      playerRef.current = null;
      setPlayer(null);
      setIsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── iframe 재생성 (GPU 메모리 누수 방지) ──────────────────
  /**
   * 현재 플레이어 iframe을 파괴하고 새로 생성합니다.
   * 페이지 리로드 없이 GPU 메모리를 완전히 해제합니다.
   * @returns 새 플레이어가 ready 되면 resolve 되는 Promise
   */
  const recreatePlayer = useCallback((): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      console.log('[useYouTubePlayer] recreatePlayer called');

      // 1. 기존 플레이어 파괴
      try {
        playerRef.current?.destroy();
        console.log('[useYouTubePlayer] Previous player destroyed');
      } catch (e) {
        console.warn('[useYouTubePlayer] destroy error (ignored):', e);
      }
      playerRef.current = null;
      setPlayer(null);
      setIsReady(false);
      lastLoadedRef.current = null;

      const wrapper = containerRef.current;
      if (!wrapper || !window.YT?.Player) {
        const errorMsg = !wrapper ? 'wrapper not found' : 'YT.Player not available';
        console.error(`[useYouTubePlayer] recreatePlayer failed: ${errorMsg}`);
        reject(new Error(errorMsg));
        return;
      }

      // 2. wrapper 내부를 비우고 새 mount div + YT.Player 생성
      wrapper.innerHTML = '';
      const mountDiv = document.createElement('div');
      mountDiv.style.width = '100%';
      mountDiv.style.height = '100%';
      wrapper.appendChild(mountDiv);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vars = { ...DEFAULT_VARS, ...extraVars } as any;

      // 타임아웃으로 플레이어 생성 실패 감지 (10초)
      const timeout = setTimeout(() => {
        console.error('[useYouTubePlayer] Player creation timeout (10s)');
        reject(new Error('Player creation timeout'));
      }, 10000);

      const opts: YTPlayerOptions = {
        width: '100%',
        height: '100%',
        playerVars: vars as YTPlayerVars,
        events: {
          onReady: (ev) => {
            clearTimeout(timeout);
            const p = ev.target;
            playerRef.current = p;
            setPlayer(p);
            setIsReady(true);
            setVolumeState(p.getVolume());
            setIsMuted(p.isMuted());
            console.log('[useYouTubePlayer] recreatePlayer: Player ready');
            cbRef.current.onReady?.(p);
            resolve();
          },
          onStateChange: (ev: YTPlayerStateChangeEvent) => {
            const s = ev.data;
            setPlayerState(s);
            cbRef.current.onStateChange?.(s, ev.target);
            if (s === YTPlayerState.ENDED) cbRef.current.onEnded?.(ev.target);
          },
          onError: (ev: YTPlayerErrorEvent) => {
            handleError(ev.data as number, ev.target);
          },
          onPlaybackQualityChange: () => {},
        },
      };

      console.log('[useYouTubePlayer] Creating new YT.Player instance');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      new window.YT.Player(mountDiv, opts as any);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  // ── 제어 함수 ──────────────────────────────────────────────
  /**
   * 영상을 즉시 로드하고 재생합니다.
   * @param videoId      - 재생할 YouTube 영상 ID
   * @param startSeconds - 재생 시작 위치(초), 기본값 0
   */
  const loadVideo = useCallback((videoId: string, startSeconds = 0) => {
    lastLoadedRef.current = { type: 'video', id: videoId };
    console.log(`[useYouTubePlayer] loadVideo: videoId=${videoId}, startSeconds=${startSeconds}`);

    if (!playerRef.current) {
      console.error('[useYouTubePlayer] loadVideo failed: playerRef.current is null');
      return;
    }

    playerRef.current.loadVideoById({ videoId, startSeconds });
    addLog('track', `▶ loadVideo: ${videoId}`);
  }, [addLog]);

  /**
   * 영상을 큐에 올리되 자동 재생하지 않습니다.
   * @param videoId      - 큐에 올릴 YouTube 영상 ID
   * @param startSeconds - 재생 시작 위치(초), 기본값 0
   */
  const cueVideo = useCallback((videoId: string, startSeconds = 0) => {
    lastLoadedRef.current = { type: 'video', id: videoId };
    console.log(`[useYouTubePlayer] cueVideo: videoId=${videoId}, startSeconds=${startSeconds}`);

    if (!playerRef.current) {
      console.error('[useYouTubePlayer] cueVideo failed: playerRef.current is null');
      return;
    }

    playerRef.current.cueVideoById({ videoId, startSeconds });
    addLog('track', `⏸ cueVideo: ${videoId}`);
  }, [addLog]);

  /**
   * 재생목록을 로드합니다.
   * @param playlistId - YouTube 재생목록 ID
   * @param index      - 시작할 재생목록 인덱스, 기본값 0
   */
  const loadPlaylist = useCallback((playlistId: string, index = 0) => {
    lastLoadedRef.current = { type: 'playlist', id: playlistId, index };
    console.log(`[useYouTubePlayer] loadPlaylist: playlistId=${playlistId}, index=${index}`);

    if (!playerRef.current) {
      console.error('[useYouTubePlayer] loadPlaylist failed: playerRef.current is null');
      return;
    }

    playerRef.current.loadPlaylist({ listType: 'playlist', list: playlistId, index });
    playerRef.current.setLoop(true);
    addLog('queue', `📋 loadPlaylist: ${playlistId} @ index ${index} (loop: on)`);
  }, [addLog]);

  /** 재생을 시작합니다. */
  const play     = useCallback(() => { playerRef.current?.playVideo();  addLog('info','▶ play');  },[addLog]);
  /** 재생을 일시정지합니다. */
  const pause    = useCallback(() => { playerRef.current?.pauseVideo(); addLog('info','⏸ pause'); },[addLog]);
  /** 재생을 중지합니다. */
  const stop     = useCallback(() => { playerRef.current?.stopVideo();  addLog('info','⏹ stop');  },[addLog]);
  /** 재생목록의 다음 영상으로 이동합니다. */
  const next     = useCallback(() => { playerRef.current?.nextVideo();     addLog('queue','⏭ next');     },[addLog]);
  /** 재생목록의 이전 영상으로 이동합니다. */
  const previous = useCallback(() => { playerRef.current?.previousVideo(); addLog('queue','⏮ previous'); },[addLog]);

  /**
   * 특정 시간으로 탐색합니다.
   * @param seconds       - 이동할 재생 위치(초)
   * @param allowSeekAhead - true 이면 버퍼링되지 않은 구간도 탐색 요청합니다.
   */
  const seekTo = useCallback((seconds: number, allowSeekAhead = true) => {
    playerRef.current?.seekTo(seconds, allowSeekAhead);
    addLog('info', `⏩ seekTo: ${seconds}s`);
  }, [addLog]);

  /**
   * 볼륨을 설정합니다. 값은 0~100 사이로 자동으로 클램핑됩니다.
   * @param vol - 설정할 볼륨 (0~100)
   */
  const setVolume = useCallback((vol: number) => {
    const v = Math.max(0, Math.min(100, vol));   // 0~100 범위로 보정합니다.
    playerRef.current?.setVolume(v);
    setVolumeState(v);
  }, []);

  /**
   * 음소거 상태를 토글합니다.
   * 현재 음소거 상태에 따라 unMute/mute 를 호출하고 React 상태를 갱신합니다.
   */
  const toggleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    if (p.isMuted()) { p.unMute(); setIsMuted(false); addLog('info','🔊 음소거 해제'); }
    else             { p.mute();   setIsMuted(true);  addLog('info','🔇 음소거');     }
  }, [addLog]);

  /**
   * 재생 속도를 설정합니다.
   * @param rate - 재생 배속 (0.25, 0.5, 1, 1.5, 2 등 YouTube 지원 값)
   */
  const setPlaybackRate = useCallback((rate: number) => {
    playerRef.current?.setPlaybackRate(rate);
    addLog('info', `⚡ ${rate}x`);
  }, [addLog]);

  /**
   * 재생 화질을 설정합니다.
   * @param q - 화질 값 (예: 'hd1080', 'hd720', 'large', 'medium', 'small', 'auto')
   */
  const setQuality = useCallback((q: YTPlaybackQuality) => {
    playerRef.current?.setPlaybackQuality(q);
    addLog('info', `📺 화질: ${q}`);
  }, [addLog]);

  /** 재생목록 반복 재생을 설정합니다. */
  const setLoop = useCallback((loop: boolean) => {
    playerRef.current?.setLoop(loop);
    addLog('info', `🔁 loop: ${loop ? 'on' : 'off'}`);
  }, [addLog]);

  /** 현재 재생 위치를 초 단위로 반환합니다. 플레이어가 없으면 0 을 반환합니다. */
  const getCurrentTime    = useCallback(() => playerRef.current?.getCurrentTime()         ?? 0, []);
  /** 영상 전체 길이를 초 단위로 반환합니다. 플레이어가 없으면 0 을 반환합니다. */
  const getDuration       = useCallback(() => playerRef.current?.getDuration()             ?? 0, []);
  /** 버퍼링된 비율(0~1)을 반환합니다. 플레이어가 없으면 0 을 반환합니다. */
  const getLoadedFraction = useCallback(() => playerRef.current?.getVideoLoadedFraction()  ?? 0, []);
  /** 현재 재생목록 인덱스를 반환합니다. 플레이어가 없으면 -1 을 반환합니다. */
  const getPlaylistIndex  = useCallback(() => playerRef.current?.getPlaylistIndex()        ?? -1, []);
  /** 현재 영상의 YouTube URL 을 반환합니다. 플레이어가 없으면 빈 문자열을 반환합니다. */
  const getVideoUrl       = useCallback(() => playerRef.current?.getVideoUrl()             ?? '', []);

  /**
   * 플레이어 인스턴스를 완전히 파괴하고 모든 상태를 초기화합니다.
   * 재사용하려면 컴포넌트를 리마운트해야 합니다.
   */
  const destroy = useCallback(() => {
    console.log('[useYouTubePlayer] destroy called');
    playerRef.current?.destroy();
    playerRef.current = null;
    setPlayer(null);
    setIsReady(false);
    addLog('warn', '🗑 플레이어 파괴');
  }, [addLog]);

  return {
    containerRef, player, playerState, isReady,
    // playerState 가 PLAYING 일 때만 isPlaying 을 true 로 노출합니다.
    isPlaying: playerState === YTPlayerState.PLAYING,
    volume, isMuted, logs,
    loadVideo, cueVideo, loadPlaylist,
    play, pause, stop, seekTo, next, previous,
    setVolume, toggleMute, setPlaybackRate, setQuality, setLoop,
    getCurrentTime, getDuration, getLoadedFraction, getPlaylistIndex, getVideoUrl, destroy,
    recreatePlayer,
  };
}